/* INTEZ TELEPHONY EVENT FIX + DIAGNOSTICS POLISH V15.6.3 */
/* INTEZ TELEPHONY ADMIN DASHBOARD V15.6.2 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Headphones,
  Network,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  RefreshCw,
  Server,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { api } from '../../api';
import type { PbxStatus } from '../types';

type DiagnosticsOperator = {
  portal_user_id: string;
  full_name: string;
  extension: string;
  browser_phone_enabled: boolean;
  allow_inbound: boolean;
  allow_outbound: boolean;
  operator_status: string;
  last_registered_at?: string | null;
  last_seen_at?: string | null;
};

type WebhookError = {
  id: string;
  provider: string;
  event_key: string;
  received_at: string;
  processing_status: string;
  attempts: number;
  call_id?: string | null;
  error?: string | null;
  processed_at?: string | null;
  payload_preview?: string | null;
};

type AdminDiagnostics = {
  settings?: { enabled?: boolean; mode?: string; secureTransport?: boolean };
  beelineSip?: {
    ready?: boolean;
    enabled?: boolean;
    lineCount?: number;
    sipUserId?: string;
    configurationStatus?: string;
  };
  pbx?: PbxStatus & { activeCalls?: number; engine?: string; version?: string };
  users?: { total?: number; enabled?: number; configured?: number; online?: number };
  operators?: DiagnosticsOperator[];
  calls?: {
    active?: number;
    active_channels?: number;
    missed_24h?: number;
    post_processing?: number;
    today_total?: number;
    today_inbound?: number;
    today_outbound?: number;
    today_answered?: number;
    today_missed?: number;
    today_duration_seconds?: number;
    avg_duration_seconds?: number;
    avg_answer_seconds?: number;
  };
  webhook?: {
    pending?: number;
    failed?: number;
    failed_recent?: number;
    last_failed_at?: string | null;
    processed?: number;
    ignored?: number;
    last_received_at?: string | null;
    errors?: WebhookError[];
  };
};

function formatDuration(value: number | undefined) {
  const seconds = Math.max(0, Number(value || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    : `${minutes}:${String(rest).padStart(2, '0')}`;
}

function formatDateTime(value?: string | null) {
  if (!value) return 'Нет данных';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Нет данных';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusTile({
  state,
  icon,
  title,
  value,
  note,
  onClick,
}: {
  state: 'ok' | 'warn' | 'neutral';
  icon: ReactNode;
  title: string;
  value: string;
  note: string;
  onClick?: () => void;
}) {
  const content = <>
    <span className="telephony-admin-status-icon">{icon}</span>
    <span><small>{title}</small><b>{value}</b><em>{note}</em></span>
  </>;
  return onClick
    ? <button type="button" className={`telephony-admin-status-tile ${state}`} onClick={onClick}>{content}</button>
    : <div className={`telephony-admin-status-tile ${state}`}>{content}</div>;
}

function Metric({
  icon,
  label,
  value,
  note,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  note: string;
}) {
  return <article className="telephony-admin-metric">
    <span>{icon}</span>
    <div><small>{label}</small><b>{value}</b><em>{note}</em></div>
  </article>;
}

export function TelephonyAdminOverview({
  onOpenDiagnostics,
  onOpenEmployees,
  onOpenCalls,
  onOpenSettings,
}: {
  onOpenDiagnostics: (tab?: 'OVERVIEW' | 'EVENTS' | 'OPERATORS' | 'CALLS' | 'INFRA') => void;
  onOpenEmployees: () => void;
  onOpenCalls: () => void;
  onOpenSettings: () => void;
}) {
  const diagnosticsQuery = useQuery<AdminDiagnostics>({
    queryKey: ['telephony-browser-diagnostics'],
    queryFn: () => api<AdminDiagnostics>('/telephony/browser/diagnostics'),
    retry: false,
    refetchInterval: 10_000,
  });

  const data = diagnosticsQuery.data;
  const pbx = data?.pbx;
  const calls = data?.calls;
  const webhook = data?.webhook;
  const operators = data?.operators || [];

  const registered = pbx?.registrationStatus === 'REGISTERED';
  const freeSwitchReady = Boolean(pbx?.gatewayReachable);
  const turnReady = Boolean(pbx?.turnReady);
  const wssReady = Boolean(String(pbx?.webSocketUrl || '').startsWith('wss://'));
  const failedHistory = Number(webhook?.failed || 0);
  const failedEvents = Number(webhook?.failed_recent || 0);
  const activeCalls = Number(pbx?.activeCalls ?? calls?.active ?? 0);
  const offlineEnabled = operators.filter(item => item.browser_phone_enabled && item.operator_status === 'OFFLINE').length;
  const configured = Number(data?.users?.configured || 0);
  const online = Number(data?.users?.online || 0);
  const missed24h = Number(calls?.missed_24h || 0);
  const postProcessing = Number(calls?.post_processing || 0);
  const recentErrors = webhook?.errors || [];

  const attention = [
    failedEvents > 0 ? {
      icon: <AlertTriangle />,
      tone: 'danger',
      title: `${failedEvents} ошибок обработки событий за последний час`,
      text: `В истории сохранено ${failedHistory}. Откройте журнал событий: там видны время, тип, попытки, Call-ID и текст ошибки.`,
      action: 'Посмотреть ошибки',
      onClick: () => onOpenDiagnostics('EVENTS'),
    } : null,
    !registered ? {
      icon: <PhoneCall />,
      tone: 'danger',
      title: 'SIP Билайн не зарегистрирован',
      text: `Текущее состояние: ${String(pbx?.registrationStatus || 'UNKNOWN')}.`,
      action: 'Открыть диагностику',
      onClick: onOpenDiagnostics,
    } : null,
    offlineEnabled > 0 ? {
      icon: <Users />,
      tone: 'warning',
      title: `${offlineEnabled} сотрудников с включённым телефоном офлайн`,
      text: 'Проверьте браузерную регистрацию и состояние рабочих мест.',
      action: 'Открыть сотрудников',
      onClick: onOpenEmployees,
    } : null,
    postProcessing > 0 ? {
      icon: <Clock3 />,
      tone: 'warning',
      title: `${postProcessing} звонков на постобработке`,
      text: 'Завершите результат звонка или проверьте зависшие состояния.',
      action: 'Открыть звонки',
      onClick: onOpenCalls,
    } : null,
    missed24h > 0 ? {
      icon: <PhoneMissed />,
      tone: 'neutral',
      title: `${missed24h} пропущенных за 24 часа`,
      text: 'Это информационный показатель. В журнале можно проверить конкретные обращения.',
      action: 'Открыть звонки',
      onClick: onOpenCalls,
    } : null,
  ].filter(Boolean) as Array<{
    icon: ReactNode;
    tone: string;
    title: string;
    text: string;
    action: string;
    onClick: () => void;
  }>;

  return <div className="telephony-admin-overview">
    <section className="surface telephony-admin-statusbar">
      <div className="telephony-admin-statusbar-head">
        <div><small>СОСТОЯНИЕ ТЕЛЕФОНИИ</small><h2>Обзор</h2><p>Ключевые показатели и проблемы, требующие внимания администратора.</p></div>
        <div className="telephony-admin-statusbar-actions">
          <button type="button" className="button ghost compact" onClick={() => void diagnosticsQuery.refetch()} disabled={diagnosticsQuery.isFetching}><RefreshCw className={diagnosticsQuery.isFetching ? 'spinning' : ''} /> Обновить</button>
          <button type="button" className="button ghost compact" onClick={onOpenSettings}><Settings /> Настройки</button>
        </div>
      </div>
      <div className="telephony-admin-status-grid">
        <StatusTile state={registered ? 'ok' : 'warn'} icon={<PhoneCall />} title="Провайдер" value={registered ? 'Билайн · REGED' : `Билайн · ${String(pbx?.registrationStatus || 'UNKNOWN')}`} note={data?.beelineSip?.sipUserId ? `SIP …${String(data.beelineSip.sipUserId).slice(-3)}` : 'Общий системный trunk'} onClick={() => onOpenDiagnostics('OVERVIEW')} />
        <StatusTile state={freeSwitchReady && wssReady && turnReady ? 'ok' : 'warn'} icon={<Server />} title="Инфраструктура" value={freeSwitchReady && wssReady && turnReady ? 'Все узлы доступны' : 'Требует проверки'} note={`FreeSWITCH · WSS · TURN`} onClick={() => onOpenDiagnostics('INFRA')} />
        <StatusTile state={configured > 0 ? 'ok' : 'warn'} icon={<Headphones />} title="Операторы" value={`${online} онлайн из ${configured}`} note={`${Number(data?.users?.enabled || 0)} телефонов включено`} onClick={onOpenEmployees} />
        <StatusTile state={activeCalls > 0 ? 'neutral' : 'ok'} icon={<Activity />} title="Сейчас" value={`${activeCalls} активных звонков`} note={`${Number(pbx?.activeChannels || 0)} каналов FreeSWITCH`} onClick={() => onOpenDiagnostics('CALLS')} />
        <StatusTile state={failedEvents > 0 ? 'warn' : 'ok'} icon={<ShieldCheck />} title="События" value={failedEvents > 0 ? `${failedEvents} ошибок за час` : 'Новых ошибок нет'} note={`История: ${failedHistory} · последнее: ${formatDateTime(webhook?.last_received_at)}`} onClick={() => onOpenDiagnostics('EVENTS')} />
      </div>
    </section>

    {diagnosticsQuery.isError && <div className="admin-feedback error">Не удалось получить административную диагностику телефонии.</div>}

    <div className="telephony-admin-overview-grid">
      <section className="surface telephony-admin-card telephony-admin-today">
        <header><div><small>СТАТИСТИКА</small><h3>Сегодня</h3><p>Агрегированные показатели по всем звонкам.</p></div><button type="button" className="soft-button" onClick={onOpenCalls}>Журнал звонков</button></header>
        <div className="telephony-admin-metrics-grid">
          <Metric icon={<PhoneCall />} label="Всего" value={Number(calls?.today_total || 0)} note="все направления" />
          <Metric icon={<PhoneIncoming />} label="Входящие" value={Number(calls?.today_inbound || 0)} note="за сегодня" />
          <Metric icon={<PhoneOutgoing />} label="Исходящие" value={Number(calls?.today_outbound || 0)} note="за сегодня" />
          <Metric icon={<CheckCircle2 />} label="Соединено" value={Number(calls?.today_answered || 0)} note="разговор установлен" />
          <Metric icon={<PhoneMissed />} label="Пропущенные" value={Number(calls?.today_missed || 0)} note="за сегодня" />
          <Metric icon={<Clock3 />} label="Время разговоров" value={formatDuration(calls?.today_duration_seconds)} note={`средний: ${formatDuration(calls?.avg_duration_seconds)}`} />
        </div>
        <div className="telephony-admin-today-footer">
          <span><small>Среднее время ответа</small><b>{formatDuration(calls?.avg_answer_seconds)}</b></span>
          <span><small>Пропущено за 24 часа</small><b>{missed24h}</b></span>
          <span><small>На постобработке</small><b>{postProcessing}</b></span>
        </div>
      </section>

      <section className="surface telephony-admin-card telephony-admin-health">
        <header><div><small>ИНФРАСТРУКТУРА</small><h3>Состояние сервисов</h3><p>Текущие проверки без перехода в технические логи.</p></div><button type="button" className="soft-button" onClick={() => onOpenDiagnostics()}>Подробнее</button></header>
        <div className="telephony-admin-health-list">
          <button type="button" onClick={() => onOpenDiagnostics()}><span className={freeSwitchReady ? 'ok' : 'warn'}><Server /></span><div><b>FreeSWITCH</b><small>{freeSwitchReady ? `${String(pbx?.engine || 'FreeSWITCH')} ${String(pbx?.version || '')} доступен` : 'Шлюз не отвечает'}</small></div><em>{freeSwitchReady ? 'Готово' : 'Ошибка'}</em></button>
          <button type="button" onClick={() => onOpenDiagnostics()}><span className={registered ? 'ok' : 'warn'}><PhoneCall /></span><div><b>Билайн SIP</b><small>{registered ? 'Общий trunk зарегистрирован' : String(pbx?.registrationStatus || 'UNKNOWN')}</small></div><em>{registered ? 'REGED' : 'Проверить'}</em></button>
          <button type="button" onClick={() => onOpenDiagnostics()}><span className={wssReady ? 'ok' : 'warn'}><ShieldCheck /></span><div><b>WebSocket / WSS</b><small>{pbx?.webSocketUrl || 'URL не определён'}</small></div><em>{wssReady ? 'Готово' : 'Проверить'}</em></button>
          <button type="button" onClick={() => onOpenDiagnostics()}><span className={turnReady ? 'ok' : 'warn'}><Network /></span><div><b>TURN / ICE</b><small>{turnReady ? `Relay ${String(pbx?.turnRange || '')}` : 'Relay требует проверки'}</small></div><em>{turnReady ? 'Готово' : 'Проверить'}</em></button>
        </div>
      </section>
    </div>

    <section className="surface telephony-admin-attention">
      <header><div><small>КОНТРОЛЬ</small><h3>Требует внимания</h3><p>Проблемы и показатели, по которым администратору стоит перейти к деталям.</p></div><span className={attention.some(item => item.tone === 'danger') ? 'danger' : attention.length ? 'warning' : 'ok'}>{attention.length}</span></header>
      {!attention.length ? <div className="telephony-admin-attention-empty"><CheckCircle2 /><div><b>Критичных проблем не обнаружено</b><small>Билайн, FreeSWITCH, WebRTC и события работают штатно.</small></div></div> : <div className="telephony-admin-attention-list">
        {attention.map((item, index) => <article key={`${item.title}-${index}`} className={item.tone}><span>{item.icon}</span><div><b>{item.title}</b><small>{item.text}</small></div><button type="button" className="button ghost compact" onClick={item.onClick}>{item.action}</button></article>)}
      </div>}
      {failedEvents > 0 && recentErrors.length > 0 && <div className="telephony-admin-recent-errors">
        <div><b>Последняя ошибка события</b><small>{formatDateTime(recentErrors[0].received_at)} · {recentErrors[0].provider || '—'} · попыток: {recentErrors[0].attempts || 1}</small></div>
        <p>{recentErrors[0].error || 'Текст ошибки не сохранён. Откройте диагностику для технических данных события.'}</p>
        <button type="button" className="soft-button" onClick={() => onOpenDiagnostics('EVENTS')}>Все ошибки</button>
      </div>}
    </section>
  </div>;
}
